import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { toast } from 'react-toastify';

const AgentSystem = () => {
  const navigate = useNavigate();
  const [agentStatus, setAgentStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState({});
  const [performanceReport, setPerformanceReport] = useState(null);
  const [senderScores, setSenderScores] = useState(null);

  const fetchAgentStatus = useCallback(async () => {
    console.log('🚀 fetchAgentStatus called');
    
    // Remove loading guard temporarily to fix stuck loading
    console.log('📝 Setting loading to TRUE');
    setLoading(true);
    setError(null);
    
    // AGGRESSIVE FAILSAFE: Force clear loading after 3 seconds NO MATTER WHAT
    const failsafeTimeout = setTimeout(() => {
      console.error('🚨 FAILSAFE: Force clearing loading after 3 seconds!');
      setLoading(false);
      setError('Request timeout - please try refreshing the page');
    }, 3000);
    
    try {
      console.log('📡 About to call apiService.getAgentStatus...');
      const response = await apiService.getAgentStatus();
      console.log('📦 Raw response received:', !!response);
      
      clearTimeout(failsafeTimeout);
      
      if (response && response.success) {
        console.log('✅ SUCCESS: Setting agent status data');
        setAgentStatus(response);
        setError(null);
      } else {
        console.error('❌ Response not successful:', response);
        setError('Failed to load agent status - invalid response');
      }
      
    } catch (error) {
      console.error('💥 CATCH block - Error:', error);
      clearTimeout(failsafeTimeout);
      setError('Error: ' + (error.message || 'Unknown error'));
    } finally {
      console.log('🏁 FINALLY: Clearing loading state');
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAgentStatus();
    // TEMPORARILY DISABLED: Auto-refresh to fix shivering/overload
    // const interval = setInterval(fetchAgentStatus, 30000);
    // return () => clearInterval(interval);
  }, [fetchAgentStatus]);

  const handleStartAgents = async () => {
    setActionLoading(prev => ({ ...prev, start: true }));
    try {
      const response = await apiService.startAgents();
      
      if (response.success) {
        toast.success('Agent system started successfully!');
        fetchAgentStatus();
      } else {
        toast.error(`Failed to start agents: ${response.message || 'Unknown error'}`);
      }
    } catch (error) {
      toast.error('Error starting agent system');
      console.error('Error starting agents:', error);
    } finally {
      setActionLoading(prev => ({ ...prev, start: false }));
    }
  };

  const handleStopAgents = async () => {
    setActionLoading(prev => ({ ...prev, stop: true }));
    try {
      const response = await apiService.stopAgents();
      
      if (response.success) {
        toast.success('Agent system stopped successfully!');
        fetchAgentStatus();
      } else {
        toast.error(`Failed to stop agents: ${response.message || 'Unknown error'}`);
      }
    } catch (error) {
      toast.error('Error stopping agent system');
      console.error('Error stopping agents:', error);
    } finally {
      setActionLoading(prev => ({ ...prev, stop: false }));
    }
  };

  const handleTriggerCycle = async (cycleType) => {
    // Prevent multiple simultaneous triggers
    if (actionLoading[cycleType]) return;
    
    setActionLoading(prev => ({ ...prev, [cycleType]: true }));
    try {
      // Add timeout protection for cycle triggers
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 20000); // 20 second max for cycles
      
      const response = await apiService.triggerAgentCycle(cycleType);
      clearTimeout(timeoutId);
      
      if (response && response.success) {
        toast.success(`${cycleType} cycle triggered successfully!`);
        // Immediately refresh to show "Active" status
        setTimeout(() => fetchAgentStatus(), 500);
      } else {
        const errorMsg = response?.error || response?.message || 'Unknown error';
        toast.error(`Failed to trigger ${cycleType} cycle: ${errorMsg}`);
        console.error(`Failed to trigger ${cycleType} cycle:`, response);
      }
    } catch (error) {
      console.error(`Error triggering ${cycleType} cycle:`, error);
      
      if (error.name === 'AbortError') {
        toast.error(`${cycleType} cycle timed out - operation cancelled`);
      } else if (error.code === 'ERR_NETWORK') {
        toast.error('Network error - check backend connection');
      } else {
        toast.error(`Error triggering ${cycleType} cycle`);
      }
    } finally {
      setActionLoading(prev => ({ ...prev, [cycleType]: false }));
    }
  };

  // NEW AUTONOMOUS AGENT MANUAL CONTROL FUNCTIONS
  
  const forceActivateAgent = async (agentType) => {
    setActionLoading(prev => ({ ...prev, [`force_${agentType}`]: true }));
    try {
      const response = await apiService.forceActivateAgent(agentType, 'Manual force activation from UI');
      
      if (response.success) {
        toast.success(`${getAgentDisplayName(agentType)} agent force activated!`);
        fetchAgentStatus();
      } else {
        toast.error(`Failed to force activate ${agentType}: ${response.message || 'Unknown error'}`);
      }
    } catch (error) {
      toast.error(`Error force activating ${agentType} agent`);
    } finally {
      setActionLoading(prev => ({ ...prev, [`force_${agentType}`]: false }));
    }
  };

  const setManualOverride = async (agentType, action) => {
    setActionLoading(prev => ({ ...prev, [`${action}_${agentType}`]: true }));
    try {
      const response = await apiService.setManualOverride(agentType, action, `Manual ${action} from UI`);
      
      if (response.success) {
        toast.success(`${getAgentDisplayName(agentType)} agent ${action} override set!`);
        fetchAgentStatus();
      } else {
        toast.error(`Failed to set ${action} override: ${response.message || 'Unknown error'}`);
      }
    } catch (error) {
      toast.error(`Error setting ${action} override for ${agentType}`);
    } finally {
      setActionLoading(prev => ({ ...prev, [`${action}_${agentType}`]: false }));
    }
  };

  const clearManualOverride = async (agentType) => {
    setActionLoading(prev => ({ ...prev, [`clear_${agentType}`]: true }));
    try {
      const response = await apiService.clearManualOverride(agentType);
      
      if (response.success) {
        toast.success(`${getAgentDisplayName(agentType)} agent returned to autonomous mode!`);
        fetchAgentStatus();
      } else {
        toast.error(`Failed to clear override: ${response.message || 'Unknown error'}`);
      }
    } catch (error) {
      toast.error(`Error clearing override for ${agentType}`);
    } finally {
      setActionLoading(prev => ({ ...prev, [`clear_${agentType}`]: false }));
    }
  };

  const getAgentIcon = (agentType) => {
    const icons = {
      sender: '📧',
      reader: '📖',
      spam_monitor: '🛡️',
      kpi_monitor: '📊'
    };
    return icons[agentType] || '🤖';
  };

  const getAgentDisplayName = (agentType) => {
    const names = {
      sender: 'Email Sender',
      reader: 'Email Reader',
      spam_monitor: 'Spam Monitor',
      kpi_monitor: 'KPI Monitor'
    };
    return names[agentType] || agentType;
  };


  const fetchPerformanceReport = async () => {
    setActionLoading(prev => ({ ...prev, report: true }));
    try {
      const response = await apiService.getPerformanceReport();
      
      if (response.success) {
        setPerformanceReport(response.data || response);
        toast.success('Performance report generated!');
      } else {
        toast.error('Failed to generate performance report');
      }
    } catch (error) {
      toast.error('Error generating performance report');
      console.error('Error generating report:', error);
    } finally {
      setActionLoading(prev => ({ ...prev, report: false }));
    }
  };

  const fetchSenderScores = async () => {
    setActionLoading(prev => ({ ...prev, scores: true }));
    try {
      const response = await apiService.getSenderScores();
      if (response.success) {
        setSenderScores(response.data || response);
        toast.success('Sender scores loaded!');
      } else {
        toast.error('Failed to load sender scores');
      }
    } catch (error) {
      toast.error('Error loading sender scores');
      console.error('Error loading sender scores:', error);
    } finally {
      setActionLoading(prev => ({ ...prev, scores: false }));
    }
  };

  const getStatusColor = (isActive, isRunning) => {
    if (!isRunning) return 'text-gray-500';
    return isActive ? 'text-green-500' : 'text-yellow-500';
  };

  const getStatusText = (isActive, isRunning) => {
    if (!isRunning) return 'Offline';
    return isActive ? 'Active' : 'Idle';
  };

  const getHealthColor = (health) => {
    switch (health) {
      case 'excellent': return 'text-green-600 bg-green-50';
      case 'good': return 'text-blue-600 bg-blue-50';
      case 'fair': return 'text-yellow-600 bg-yellow-50';
      case 'needs_attention': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner mr-2"></div>
        <span>Loading agent system status...</span>
      </div>
    );
  }

  console.log('🔍 DEBUG: agentStatus =', agentStatus);
  const isRunning = agentStatus?.data?.system_status?.system_running;
  const systemMetrics = agentStatus?.data?.system_status?.system_metrics || agentStatus?.data?.system_metrics || {};
  const agentStatuses = agentStatus?.data?.system_status?.agent_statuses || agentStatus?.data?.agent_statuses || {};
  console.log('🔍 DEBUG: isRunning =', isRunning, 'systemMetrics =', systemMetrics);

  return (
    <div className="space-y-4 sm:space-y-6 fade-in mt-20">
        {/* System Overview */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
            <div className="mb-4 md:mb-0">
              <h2 className="text-2xl font-bold text-gray-900">🤖 Autonomous Agent System</h2>
              <p className="text-gray-600 mt-1">
                AI-powered email warmup with human-like behavior patterns
              </p>
            </div>
            <div className="flex items-center">
              <div className={`px-4 py-2 rounded-lg font-medium ${isRunning ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                <div className="flex items-center">
                  <div className={`w-3 h-3 rounded-full mr-2 ${isRunning ? 'bg-green-400' : 'bg-red-400'}`}></div>
                  {isRunning ? 'System Online' : 'System Offline'}
                </div>
              </div>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-red-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <h3 className="text-sm font-medium text-red-800">System Error</h3>
                  <p className="text-sm text-red-700 mt-1">{error}</p>
                </div>
                <button
                  onClick={() => setError(null)}
                  className="ml-auto text-red-400 hover:text-red-600"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {/* Force Refresh Button - Emergency Override */}
          {loading && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="loading-spinner mr-3"></div>
                  <div>
                    <h3 className="text-sm font-medium text-yellow-800">Loading Agent Data</h3>
                    <p className="text-sm text-yellow-700">If this takes too long, try force refresh</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    console.log('🔄 FORCE REFRESH: Manually clearing loading state');
                    setLoading(false);
                    setError(null);
                    setTimeout(() => fetchAgentStatus(), 500);
                  }}
                  className="px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 text-sm font-medium"
                >
                  Force Refresh
                </button>
              </div>
            </div>
          )}


          {/* System Controls */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
            <button
              onClick={handleStartAgents}
              disabled={isRunning || actionLoading.start}
              className="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm transition-colors duration-200"
              title="Start 4 autonomous agents that will intelligently send emails, process replies, monitor spam, and track KPIs"
            >
              {actionLoading.start ? (
                <div className="flex items-center justify-center">
                  <div className="loading-spinner-small mr-2"></div>
                  Starting...
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  <span>🚀 Start Agents</span>
                  <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 9l3 3m0 0l-3 3m3-3H8m13 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              )}
            </button>

            <button
              onClick={handleStopAgents}
              disabled={!isRunning || actionLoading.stop}
              className="w-full px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm transition-colors duration-200"
            >
              {actionLoading.stop ? (
                <div className="flex items-center justify-center">
                  <div className="loading-spinner-small mr-2"></div>
                  Stopping...
                </div>
              ) : (
                '🛑 Stop Agents'
              )}
            </button>

            <button
              onClick={fetchPerformanceReport}
              disabled={!isRunning || actionLoading.report}
              className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm transition-colors duration-200"
            >
              {actionLoading.report ? (
                <div className="flex items-center justify-center">
                  <div className="loading-spinner-small mr-2"></div>
                  Loading...
                </div>
              ) : (
                '📊 Report'
              )}
            </button>

            <button
              onClick={fetchSenderScores}
              disabled={!isRunning || actionLoading.scores}
              className="w-full px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm transition-colors duration-200"
            >
              {actionLoading.scores ? (
                <div className="flex items-center justify-center">
                  <div className="loading-spinner-small mr-2"></div>
                  Loading...
                </div>
              ) : (
                '🏆 Scores'
              )}
            </button>
        </div>

        {/* Detailed Tracking Section */}
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div className="mb-4 sm:mb-0">
              <h3 className="font-semibold text-blue-900">📊 Want to see exactly what will happen?</h3>
              <p className="text-blue-800 text-sm">View detailed email sending schedule, recipient lists, and timing analysis</p>
            </div>
            <button
              onClick={() => navigate('/email-tracking')}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium text-sm flex items-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <span>View Email Tracking</span>
            </button>
          </div>
        </div>

        {/* System Metrics */}
        {isRunning && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
            <div className="bg-blue-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-blue-700">{systemMetrics.total_emails_sent || 0}</div>
              <div className="text-sm text-blue-600">Emails Sent</div>
            </div>
            <div className="bg-green-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-green-700">{systemMetrics.total_emails_read || 0}</div>
              <div className="text-sm text-green-600">Emails Read</div>
            </div>
            <div className="bg-yellow-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-yellow-700">{agentStatuses.reader?.replies_sent || 0}</div>
              <div className="text-sm text-yellow-600">Replies Sent</div>
            </div>
            <div className="bg-purple-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-purple-700">{systemMetrics.total_emails_rescued || 0}</div>
              <div className="text-sm text-purple-600">Spam Rescued</div>
            </div>
            <div className={`rounded-lg p-4 text-center ${getHealthColor(systemMetrics.system_health || 'good')}`}>
              <div className="text-2xl font-bold">{systemMetrics.avg_warmup_score || 0}</div>
              <div className="text-sm">Avg Score</div>
            </div>
          </div>
        )}
        </div>

        {/* Autonomous Agent Intelligence Status */}
        {isRunning && agentStatus && agentStatus.system_status?.system_intelligence && (
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6 mb-6 border border-blue-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900 flex items-center">
                🤖 Autonomous Intelligence Status
              </h2>
              <div className="flex items-center space-x-2">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  agentStatus.system_status?.system_intelligence?.autonomous_mode 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {agentStatus.system_status?.system_intelligence?.autonomous_mode ? '🤖 Fully Autonomous' : '🎛️ Manual Control'}
                </span>
                {agentStatus.system_status?.active_manual_overrides > 0 && (
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                    {agentStatus.system_status?.active_manual_overrides} Manual Overrides Active
                  </span>
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {Object.entries(agentStatus.system_status?.agent_intelligence || {}).map(([agentType, intelligence]) => (
                <div key={`agent-${agentType}`} className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900 capitalize text-sm">
                      {getAgentIcon(agentType)} {getAgentDisplayName(agentType)}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded ${
                      intelligence.autonomous_mode 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {intelligence.autonomous_mode ? 'Auto' : 'Manual'}
                    </span>
                  </div>
                  
                  {intelligence.recent_decisions && intelligence.recent_decisions.length > 0 && (
                    <div className="text-xs text-gray-600 mt-2">
                      <div className="font-medium">Latest Decision:</div>
                      <div className="truncate">
                        {intelligence.recent_decisions[intelligence.recent_decisions.length - 1]?.decision}: {' '}
                        {intelligence.recent_decisions[intelligence.recent_decisions.length - 1]?.reason}
                      </div>
                    </div>
                  )}
                  
                  {intelligence.manual_override && (
                    <div className="text-xs bg-orange-50 text-orange-700 p-2 rounded mt-2">
                      Override: {intelligence.manual_override.action} - {intelligence.manual_override.reason}
                    </div>
                  )}
                  
                  <div className="flex space-x-1 mt-3">
                    <button
                      onClick={() => forceActivateAgent(agentType)}
                      disabled={actionLoading[`force_${agentType}`]}
                      className="flex-1 px-2 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600 disabled:opacity-50"
                    >
                      {actionLoading[`force_${agentType}`] ? '...' : 'Force'}
                    </button>
                    <button
                      onClick={() => setManualOverride(agentType, 'pause')}
                      disabled={actionLoading[`pause_${agentType}`]}
                      className="flex-1 px-2 py-1 bg-yellow-500 text-white text-xs rounded hover:bg-yellow-600 disabled:opacity-50"
                    >
                      {actionLoading[`pause_${agentType}`] ? '...' : 'Pause'}
                    </button>
                    <button
                      onClick={() => clearManualOverride(agentType)}
                      disabled={actionLoading[`clear_${agentType}`]}
                      className="flex-1 px-2 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600 disabled:opacity-50"
                    >
                      {actionLoading[`clear_${agentType}`] ? '...' : 'Auto'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Individual Agents Status */}
      {isRunning && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 sm:gap-6">
          {/* Email Sender Agent */}
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900">📧 Email Sender</h3>
              <span className={`text-sm ${getStatusColor(agentStatuses.sender?.active, isRunning)}`}>
                {getStatusText(agentStatuses.sender?.active, isRunning)}
              </span>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Daily Sent:</span>
                <span className="font-medium">{agentStatuses.sender?.daily_sent || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Cycles:</span>
                <span className="font-medium">{agentStatuses.sender?.cycles || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Business Hours:</span>
                <span className="font-medium">{agentStatuses.sender?.business_hours ? 'Yes' : 'No'}</span>
              </div>
            </div>
            <button
              onClick={() => handleTriggerCycle('sender')}
              disabled={actionLoading.sender}
              className="w-full mt-3 px-3 py-2 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 disabled:opacity-50 text-sm"
            >
              {actionLoading.sender ? 'Triggering...' : 'Trigger Sending'}
            </button>
          </div>

          {/* Email Reader Agent */}
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900">📖 Email Reader</h3>
              <span className={`text-sm ${getStatusColor(agentStatuses.reader?.active, isRunning)}`}>
                {getStatusText(agentStatuses.reader?.active, isRunning)}
              </span>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Daily Read:</span>
                <span className="font-medium">{agentStatuses.reader?.daily_read || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Replies Sent:</span>
                <span className="font-medium">{agentStatuses.reader?.replies_sent || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Scheduled:</span>
                <span className="font-medium">{agentStatuses.reader?.scheduled || 0}</span>
              </div>
            </div>
            <button
              onClick={() => handleTriggerCycle('reader')}
              disabled={actionLoading.reader}
              className="w-full mt-3 px-3 py-2 bg-green-100 text-green-700 rounded hover:bg-green-200 disabled:opacity-50 text-sm"
            >
              {actionLoading.reader ? 'Triggering...' : 'Trigger Reading'}
            </button>
          </div>

          {/* Reply Agent */}
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900">💬 Reply Agent</h3>
              <span className={`text-sm ${getStatusColor(agentStatuses.reply_agent?.active, isRunning)}`}>
                {getStatusText(agentStatuses.reply_agent?.active, isRunning)}
              </span>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Daily Replies:</span>
                <span className="font-medium">{agentStatuses.reply_agent?.daily_replies || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Reply Rate:</span>
                <span className="font-medium">{agentStatuses.reply_agent?.reply_rate || '0%'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Emails Processed:</span>
                <span className="font-medium">{agentStatuses.reply_agent?.emails_processed || 0}</span>
              </div>
            </div>
            <button
              onClick={() => handleTriggerCycle('reply')}
              disabled={actionLoading.reply}
              className="w-full mt-3 px-3 py-2 bg-indigo-100 text-indigo-700 rounded hover:bg-indigo-200 disabled:opacity-50 text-sm"
            >
              {actionLoading.reply ? 'Replying...' : 'Generate Replies'}
            </button>
          </div>

          {/* Spam Monitor Agent */}
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900">🛡️ Spam Monitor</h3>
              <span className={`text-sm ${getStatusColor(agentStatuses.spam_monitor?.active, isRunning)}`}>
                {getStatusText(agentStatuses.spam_monitor?.active, isRunning)}
              </span>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Daily Rescued:</span>
                <span className="font-medium">{agentStatuses.spam_monitor?.daily_rescued || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Folders Checked:</span>
                <span className="font-medium">{agentStatuses.spam_monitor?.folders_checked || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Monitoring:</span>
                <span className="font-medium">{agentStatuses.spam_monitor?.monitoring ? 'Yes' : 'No'}</span>
              </div>
            </div>
            <button
              onClick={() => handleTriggerCycle('spam')}
              disabled={actionLoading.spam}
              className="w-full mt-3 px-3 py-2 bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200 disabled:opacity-50 text-sm"
            >
              {actionLoading.spam ? 'Scanning...' : 'Scan Spam Folders'}
            </button>
          </div>

          {/* KPI Monitor Agent */}
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900">📈 KPI Monitor</h3>
              <span className={`text-sm ${getStatusColor(agentStatuses.kpi_monitor?.active, isRunning)}`}>
                {getStatusText(agentStatuses.kpi_monitor?.active, isRunning)}
              </span>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Accounts Analyzed:</span>
                <span className="font-medium">{agentStatuses.kpi_monitor?.accounts_analyzed || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Active Alerts:</span>
                <span className="font-medium">{agentStatuses.kpi_monitor?.active_alerts || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Reports:</span>
                <span className="font-medium">{agentStatuses.kpi_monitor?.reports || 0}</span>
              </div>
            </div>
            <button
              onClick={() => handleTriggerCycle('kpi')}
              disabled={actionLoading.kpi}
              className="w-full mt-3 px-3 py-2 bg-purple-100 text-purple-700 rounded hover:bg-purple-200 disabled:opacity-50 text-sm"
            >
              {actionLoading.kpi ? 'Analyzing...' : 'Run Analysis'}
            </button>
          </div>
        </div>
      )}

      {/* Manual Cycle Triggers */}
      {isRunning && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Manual Cycle Controls</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <button
              onClick={() => handleTriggerCycle('full')}
              disabled={actionLoading.full}
              className="px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {actionLoading.full ? (
                <div className="flex items-center justify-center">
                  <div className="loading-spinner-small mr-2"></div>
                  Running Full Cycle...
                </div>
              ) : (
                <div>
                  <div className="text-lg">🔄 Full Cycle</div>
                  <div className="text-xs opacity-75">All agents execute</div>
                </div>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Performance Report Display */}
      {performanceReport && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">📊 Performance Report</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            <div>
              <h4 className="font-medium text-gray-700 mb-3">System Overview</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>System Health:</span>
                  <span className={`px-2 py-1 rounded text-xs ${getHealthColor(performanceReport.performance_summary === 'System operational' ? 'excellent' : 'needs_attention')}`}>
                    {performanceReport.performance_summary === 'System operational' ? 'Excellent' : performanceReport.performance_summary || 'Unknown'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Reply Rate:</span>
                  <span className="text-green-600 font-semibold">{(performanceReport.system_metrics?.reply_rate_percent || 0).toFixed(1)}%</span>
                </div>
                <div className="flex justify-between">
                  <span>Total Emails:</span>
                  <span>{performanceReport.system_metrics?.total_emails_sent || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span>Replies Sent:</span>
                  <span className="text-blue-600 font-semibold">{performanceReport.system_metrics?.total_replies_sent || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span>Avg Score:</span>
                  <span className={`font-semibold ${(performanceReport.system_metrics?.average_sender_score || 0) > 50 ? 'text-green-600' : 'text-orange-600'}`}>
                    {(performanceReport.system_metrics?.average_sender_score || 0)}/100
                  </span>
                </div>
              </div>
            </div>
            
            {performanceReport.recommendations && performanceReport.recommendations.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-700 mb-3">Recommendations</h4>
                <ul className="space-y-1 text-sm">
                  {performanceReport.recommendations.slice(0, 5).map((rec, index) => (
                    <li key={`recommendation-${index}-${rec.substring(0, 20)}`} className="flex items-start">
                      <span className="text-blue-500 mr-2">•</span>
                      <span>{rec}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sender Scores Display */}
      {senderScores && Object.keys(senderScores).length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">🏆 Sender Scores</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(senderScores).map(([account, scoreData]) => (
              <div key={`sender-score-${account}`} className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-medium text-gray-900 truncate">{account}</div>
                  <div className={`px-2 py-1 rounded text-xs font-bold ${
                    scoreData.score >= 80 ? 'bg-green-100 text-green-800' :
                    scoreData.score >= 60 ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {scoreData.grade}
                  </div>
                </div>
                <div className="text-2xl font-bold text-gray-900">{scoreData.score?.toFixed(1)}</div>
                <div className="text-xs text-gray-500">
                  Updated: {scoreData.last_updated ? new Date(scoreData.last_updated).toLocaleString() : 'Never'}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentSystem;
